import { Link } from "react-router-dom";
import { Wallet, Scale, HelpCircle, Check } from "lucide-react";
import { PageHero, Breadcrumbs, FadeIn } from "@/components/Shared";

const OPTIONS = [
  {
    icon: Wallet,
    title: "Privately funded",
    text: "For clients who fund their own defence, we provide a bespoke, partner-led service. We will give you a clear explanation of how our fees are calculated at the outset, agree the basis of charging before any work begins, and keep you informed of costs throughout your case — no surprises.",
    points: [
      "Clear fee basis agreed before work begins",
      "Partner-led, bespoke service",
      "Regular cost updates throughout your case",
      "Fixed fees available for defined stages where appropriate",
    ],
  },
  {
    icon: Scale,
    title: "Legal aid",
    text: "We are committed to publicly funded work and hold our own contract with the Legal Aid Agency. Advice and assistance at the police station is free for everyone, regardless of income. For court proceedings, legal aid is subject to a merits test and a means test — we will assess your eligibility and handle the application for you.",
    points: [
      "Police station advice is free for everyone — always",
      "Our own Legal Aid Agency client contract",
      "We assess eligibility and complete applications for you",
      "Crown Court legal aid may involve means-tested contributions",
    ],
  },
  {
    icon: HelpCircle,
    title: "Not sure?",
    text: "Many clients don't know how their case will be funded when they first call — that is completely normal. Tell us your situation and we will explain your options clearly, including whether you qualify for legal aid and what private representation would cost.",
    points: [
      "Free initial discussion of your funding options",
      "Honest advice on legal aid eligibility",
      "Clear private fee estimates before you commit",
    ],
  },
];

export default function FeesFunding() {
  return (
    <main data-testid="fees-page">
      <PageHero
        eyebrow="Fees & funding"
        title="Clear about costs, from the first call."
        lead="We defend both publicly and privately funded clients. Whatever your funding, you will receive the same exacting standard of representation."
      />
      <Breadcrumbs items={[{ label: "Fees & Funding" }]} />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {OPTIONS.map((opt, i) => (
              <FadeIn key={i} delay={i * 0.1} className="h-full">
                <div className="flex h-full flex-col border border-slate-200 bg-white p-8" data-testid={`funding-option-${i}`}>
                  <opt.icon className="size-7 text-brand-gold" />
                  <h2 className="mt-6 text-xl font-medium text-brand-navy">{opt.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{opt.text}</p>
                  <ul className="mt-6 flex-1 space-y-2.5 border-t border-slate-100 pt-6">
                    {opt.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <Check className="mt-0.5 size-4 shrink-0 text-brand-gold" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.2}>
            <div className="brand-panel-dark mt-12 p-8 sm:p-12">
              <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-2xl font-light tracking-tight">
                    Talk to us about funding before you worry about it.
                  </h2>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-300">
                    One conversation will tell you exactly where you stand. We treat
                    all enquiries in the strictest confidence.
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="brand-cta-primary shrink-0 px-8 py-4 tracking-[0.2em]"
                  data-testid="fees-contact-cta"
                >
                  Get in touch
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
